import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { DATABASES } from "../utils/databases";
import { groupLabel } from "../utils/identity";
import GroupPicker from "./GroupPicker";
import "./AdminCollections.css";

// Access-rule subject types (backend: CollectionRuleIn.rule_type) and their
// label keys. A group rule's subject is a group id chosen with GroupPicker.
const RULE_GROUP = "group";
const RULE_TYPE_LABELS = {
  user: "adminCollections.ruleTypeUser",
  role: "adminCollections.ruleTypeRole",
  [RULE_GROUP]: "adminCollections.ruleTypeGroup",
};

/** Admin "Collections" page (Norma feature 4): create/rename/delete collections,
 * manage member documents + access rules, and file the workspace's unassigned
 * documents into a collection. Mirrors AuditLog.jsx/AdminUsers.jsx conventions:
 * generation refs discard stale responses, API errors are shown inline and never
 * clear the previously loaded list, destructive actions sit behind an inline
 * confirm, and the unassigned list is cursor-paginated ("load more"). */
export default function AdminCollections() {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState("collections");   // "collections" | "unassigned"
  const [database, setDatabase] = useState("");

  // ── collections list + create form ───────────────────────────────────
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newDatabase, setNewDatabase] = useState("");
  const [newLicence, setNewLicence] = useState("");
  const [creating, setCreating] = useState(false);

  // ── collection detail ────────────────────────────────────────────────
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [ruleType, setRuleType] = useState("user");
  const [ruleSubject, setRuleSubject] = useState("");
  const [ruleGroup, setRuleGroup] = useState(null);       // the group picked for a "group" rule
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editLicence, setEditLicence] = useState("");
  const [editing, setEditing] = useState(false);

  // ── display lookups (member filenames, rule-subject emails) ──────────
  const [docsByName, setDocsByName] = useState({});
  const [usersByName, setUsersByName] = useState({});

  // ── unassigned view ──────────────────────────────────────────────────
  const [unassigned, setUnassigned] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [moveTo, setMoveTo] = useState("");
  const [moving, setMoving] = useState(false);

  const generation = useRef(0);

  const loadCollections = useCallback(async (gen) => {
    const params = database ? `?database=${encodeURIComponent(database)}` : "";
    try {
      const data = await apiFetchJson(`/api/v1/collections${params}`);
      if (gen !== generation.current) return;
      setCollections(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      if (gen !== generation.current) return;
      setError(err.message);
    }
  }, [database]);

  const loadUnassigned = useCallback(async (gen, cursor) => {
    // Page size comes from the backend default (COLLECTIONS_UNASSIGNED_PAGE_SIZE)
    // so the value stays env-configurable in one place.
    const params = new URLSearchParams();
    if (database) params.set("database", database);
    if (cursor) params.set("after", cursor);
    try {
      const data = await apiFetchJson(`/api/v1/collections/unassigned?${params.toString()}`);
      if (gen !== generation.current) return;
      setUnassigned((prev) => (cursor ? [...prev, ...data.documents] : data.documents));
      setNextCursor(data.next_cursor);
      setError("");
    } catch (err) {
      if (gen !== generation.current) return;
      setError(err.message);
    }
  }, [database]);

  useEffect(() => {
    generation.current += 1;
    const gen = generation.current;
    setNextCursor(null);
    setUnassigned([]);
    setOpenId(null);
    setDetail(null);
    setLoading(true);
    loadCollections(gen).finally(() => {
      if (gen === generation.current) setLoading(false);
    });
    if (subTab === "unassigned") loadUnassigned(gen, null);
    return () => {
      generation.current += 1;
    };
  }, [loadCollections, loadUnassigned, subTab, database]);

  // Resolve document ids → filenames for the opened collection's member list.
  useEffect(() => {
    let stale = false;
    const params = database ? `?database=${encodeURIComponent(database)}` : "";
    apiFetchJson(`/api/v1/documents${params}`)
      .then((data) => {
        if (!stale) {
          setDocsByName(Object.fromEntries(
            (data.documents || []).map((d) => [d.document_id, d.filename])));
        }
      })
      .catch(() => {
        if (!stale) setDocsByName({});
      });
    return () => {
      stale = true;
    };
  }, [database]);

  // Resolve rule subject ids → emails for the opened collection's rules.
  useEffect(() => {
    let stale = false;
    apiFetchJson("/api/v1/auth/users")
      .then((users) => {
        if (!stale) setUsersByName(Object.fromEntries(users.map((u) => [u.id, u.email])));
      })
      .catch(() => {
        if (!stale) setUsersByName({});
      });
    return () => {
      stale = true;
    };
  }, []);

  const changeRuleType = (next) => {
    setRuleType(next);
    setRuleSubject("");
    setRuleGroup(null);
  };


  const openDetail = async (id) => {
    setOpenId(id);
    setDetail(null);
    setConfirmDelete(false);
    const gen = generation.current;
    try {
      const data = await apiFetchJson(`/api/v1/collections/${id}`);
      if (gen !== generation.current) return;
      setDetail(data);
      setError("");
    } catch (err) {
      if (gen !== generation.current) return;
      setError(err.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      await apiFetchJson("/api/v1/collections", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          database: newDatabase || null,
          licence: newLicence.trim() || null,
        }),
      });
      setName("");
      setDescription("");
      setNewDatabase("");
      setNewLicence("");
      await loadCollections(generation.current);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!openId) return;
    setBusy(true);
    setError("");
    const id = openId;
    try {
      await apiFetchJson(`/api/v1/collections/${id}`, { method: "DELETE" });
      setOpenId(null);
      setDetail(null);
      setConfirmDelete(false);
      await loadCollections(generation.current);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const ruleSubjectValue = ruleType === RULE_GROUP ? (ruleGroup ? ruleGroup.id : "") : ruleSubject.trim();

  const handleAddRule = async (e) => {
    e.preventDefault();
    if (!ruleSubjectValue) return;
    const id = openId;
    setBusy(true);
    setError("");
    try {
      await apiFetchJson(`/api/v1/collections/${id}/rules`, {
        method: "POST",
        body: JSON.stringify({ rule_type: ruleType, subject: ruleSubjectValue }),
      });
      setRuleSubject("");
      setRuleGroup(null);
      await openDetail(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeRule = async (ruleId) => {
    const id = openId;
    setBusy(true);
    setError("");
    try {
      await apiFetchJson(`/api/v1/collections/${id}/rules/${ruleId}`, { method: "DELETE" });
      await openDetail(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const startEdit = () => {
    if (!detail) return;
    setEditName(detail.name || "");
    setEditDescription(detail.description || "");
    setEditLicence(detail.licence || "");
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    const id = openId;
    setBusy(true);
    setError("");
    try {
      await apiFetchJson(`/api/v1/collections/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editName.trim(), description: editDescription.trim(), licence: editLicence.trim() }),
      });
      setEditing(false);
      await openDetail(id);
      await loadCollections(generation.current);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeDocument = async (documentId) => {
    const id = openId;
    setBusy(true);
    setError("");
    try {
      await apiFetchJson(`/api/v1/collections/${id}/documents/${documentId}`, { method: "DELETE" });
      await openDetail(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadMore = async () => {
    const gen = generation.current;
    setLoadingMore(true);
    try {
      await loadUnassigned(gen, nextCursor);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleSelect = (documentId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  const moveSelected = async () => {
    if (selected.size === 0 || !moveTo) return;
    const gen = generation.current;
    setMoving(true);
    setError("");
    try {
      await apiFetchJson(`/api/v1/collections/${moveTo}/documents`, {
        method: "POST",
        body: JSON.stringify({ document_ids: [...selected] }),
      });
      if (gen !== generation.current) return;
      setSelected(new Set());
      setMoveTo("");
      await loadUnassigned(gen, null);
    } catch (err) {
      if (gen === generation.current) setError(err.message);
    } finally {
      setMoving(false);
    }
  };

  // Group rules carry the backend-resolved `subject_label` (group name or
  // external id); user rules resolve to the email. Unknown subjects show the raw id.
  const subjectLabel = (rule) => {
    if (rule.rule_type === RULE_GROUP) return rule.subject_label || rule.subject_id;
    return usersByName[rule.subject_id] || rule.subject_id;
  };
  const ruleTypeLabel = (type) => (RULE_TYPE_LABELS[type] ? t(RULE_TYPE_LABELS[type]) : type);
  const moveLabel = moving ? t("adminCollections.moving") : t("adminCollections.moveSelected");

  return (
    <div className="ds-page">
      <header className="ds-page-header">
        <p className="ds-page-subtitle">{t("adminCollections.subtitle")}</p>
      </header>

      <div className="ds-toolbar">
        <label className="ds-field">
          <span className="ds-label">{t("adminCollections.workspacesLabel")}</span>
          <select
            className="ds-select"
            aria-label={t("adminCollections.workspacesLabel")}
            value={database}
            onChange={(e) => setDatabase(e.target.value)}
          >
            <option value="">{t("adminCollections.allWorkspaces")}</option>
            {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <div className="ac-subtabs">
          <button
            type="button"
            className="ds-chip"
            aria-pressed={subTab === "collections"}
            onClick={() => setSubTab("collections")}
          >
            {t("adminCollections.collectionsTab")}
          </button>
          <button
            type="button"
            className="ds-chip"
            aria-pressed={subTab === "unassigned"}
            onClick={() => setSubTab("unassigned")}
          >
            {t("adminCollections.unassignedTab")}
          </button>
        </div>
      </div>

      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}

      {subTab === "collections" ? (
        <div className="ac-collections-view">
          <form className="ds-card ac-form" onSubmit={handleCreate}>
            <div className="ds-field">
              <label className="ds-label" htmlFor="ac-name">{t("adminCollections.name")}</label>
              <input id="ac-name" className="ds-input" value={name} onChange={(e) => setName(e.target.value)} disabled={creating} maxLength={200} />
            </div>
            <div className="ds-field">
              <label className="ds-label" htmlFor="ac-description">{t("adminCollections.description")}</label>
              <input id="ac-description" className="ds-input" value={description} onChange={(e) => setDescription(e.target.value)} disabled={creating} />
            </div>
            <div className="ds-field">
              <label className="ds-label" htmlFor="ac-database">{t("adminCollections.createWorkspace")}</label>
              <select
                id="ac-database"
                className="ds-select"
                aria-label={t("adminCollections.createWorkspace")}
                value={newDatabase}
                onChange={(e) => setNewDatabase(e.target.value)}
                disabled={creating}
              >
                <option value="">{t("adminCollections.allWorkspaces")}</option>
                {DATABASES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="ds-field">
              <label className="ds-label" htmlFor="ac-licence">{t("adminCollections.licence")}</label>
              <input
                id="ac-licence"
                className="ds-input"
                value={newLicence}
                onChange={(e) => setNewLicence(e.target.value)}
                placeholder={t("adminCollections.licencePlaceholder")}
                maxLength={200}
                disabled={creating}
              />
            </div>
            <button type="submit" className="ds-btn ds-btn--primary" disabled={creating || !name.trim()}>
              {creating ? t("adminCollections.creating") : t("adminCollections.create")}
            </button>
          </form>

          {loading ? (
            <div className="ds-empty">{t("adminCollections.loading")}</div>
          ) : collections.length === 0 ? (
            <div className="ds-empty">{t("adminCollections.emptyCollections")}</div>
          ) : (
            <div className="ds-list">
              {collections.map((c) => (
                <div key={c.id} className="ds-list-row">
                  <button
                    type="button"
                    className="ds-list-toggle"
                    aria-expanded={openId === c.id}
                    onClick={() => (openId === c.id ? setOpenId(null) : openDetail(c.id))}
                  >
                    <span className="ds-list-title">{c.name}</span>
                    <span className="ds-meta ac-row-end">
                      {typeof c.document_count === "number"
                        ? t("adminCollections.documentsCount", { count: c.document_count })
                        : ""}
                      {c.database ? ` · ${c.database}` : ""}
                    </span>
                  </button>
                  {openId === c.id && detail && (
                    <div className="ds-list-detail">
                      {detail.description && <p className="ac-detail-description">{detail.description}</p>}
                      {detail.licence && (
                        <p className="ac-detail-description">{t("adminCollections.licence")}: {detail.licence}</p>
                      )}
                      <div className="ac-detail-section">
                        {editing ? (
                          <form className="ac-form ac-form-inline" onSubmit={saveEdit}>
                            <div className="ds-field">
                              <label className="ds-label" htmlFor="ac-edit-name">{t("adminCollections.editNameLabel")}</label>
                              <input id="ac-edit-name" className="ds-input" value={editName} onChange={(e) => setEditName(e.target.value)} disabled={busy} maxLength={200} />
                            </div>
                            <div className="ds-field">
                              <label className="ds-label" htmlFor="ac-edit-description">{t("adminCollections.editDescriptionLabel")}</label>
                              <input id="ac-edit-description" className="ds-input" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} disabled={busy} />
                            </div>
                            <div className="ds-field">
                              <label className="ds-label" htmlFor="ac-edit-licence">{t("adminCollections.licence")}</label>
                              <input id="ac-edit-licence" className="ds-input" value={editLicence} onChange={(e) => setEditLicence(e.target.value)} disabled={busy} maxLength={200} />
                            </div>
                            <button type="submit" className="ds-btn ds-btn--primary ds-btn--sm" disabled={busy || !editName.trim()}>
                              {busy ? t("adminCollections.saving") : t("adminCollections.save")}
                            </button>
                            <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" disabled={busy} onClick={() => setEditing(false)}>
                              {t("adminCollections.cancel")}
                            </button>
                          </form>
                        ) : (
                          <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={startEdit}>
                            {t("adminCollections.edit")}
                          </button>
                        )}
                      </div>
                      <div className="ac-detail-section">
                        <h4 className="ac-detail-heading">{t("adminCollections.members")}</h4>
                        {(detail.document_ids || []).length === 0 && (
                          <div className="ds-empty ac-empty-inline">{t("adminCollections.noDocuments")}</div>
                        )}
                        {(detail.document_ids || []).map((documentId) => (
                          <div key={documentId} className="ac-item-row">
                            <span>{docsByName[documentId] || documentId}</span>
                            <button
                              type="button"
                              className="ds-btn ds-btn--danger ds-btn--sm"
                              disabled={busy}
                              aria-label={`${t("adminCollections.removeDocument")} ${docsByName[documentId] || documentId}`}
                              onClick={() => removeDocument(documentId)}
                            >
                              {t("adminCollections.remove")}
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="ac-detail-section">
                        <h4 className="ac-detail-heading">{t("adminCollections.accessRules")}</h4>
                        {(detail.rules || []).length === 0 && (
                          <div className="ds-empty ac-empty-inline">{t("adminCollections.noRules")}</div>
                        )}
                        {(detail.rules || []).map((rule) => (
                          <div key={rule.id} className="ac-item-row">
                            <span>{ruleTypeLabel(rule.rule_type)}: {subjectLabel(rule)}</span>
                            <button
                              type="button"
                              className="ds-btn ds-btn--danger ds-btn--sm"
                              disabled={busy}
                              aria-label={`${t("adminCollections.removeRule")} ${subjectLabel(rule)}`}
                              onClick={() => removeRule(rule.id)}
                            >
                              {t("adminCollections.remove")}
                            </button>
                          </div>
                        ))}
                      </div>
                      <form className="ac-form ac-form-inline ac-detail-section" onSubmit={handleAddRule}>
                        <select
                          className="ds-select"
                          aria-label={t("adminCollections.ruleType")}
                          value={ruleType}
                          onChange={(e) => changeRuleType(e.target.value)}
                          disabled={busy}
                        >
                          {Object.keys(RULE_TYPE_LABELS).map((type) => (
                            <option key={type} value={type}>{t(RULE_TYPE_LABELS[type])}</option>
                          ))}
                        </select>
                        {ruleType === RULE_GROUP ? (
                          <>
                            <GroupPicker value={ruleGroup ? ruleGroup.id : null} onSelect={setRuleGroup} disabled={busy} />
                            {ruleGroup && (
                              <span className="ds-meta">
                                {t("adminCollections.selectedGroup", { group: groupLabel(ruleGroup) })}
                              </span>
                            )}
                          </>
                        ) : (
                          <input
                            className="ds-input ac-grow"
                            aria-label={t("adminCollections.ruleSubject")}
                            value={ruleSubject}
                            onChange={(e) => setRuleSubject(e.target.value)}
                            placeholder={t("adminCollections.ruleSubjectPlaceholder")}
                            disabled={busy}
                          />
                        )}
                        <button type="submit" className="ds-btn ds-btn--primary ds-btn--sm" disabled={busy || !ruleSubjectValue}>
                          {t("adminCollections.addRule")}
                        </button>
                      </form>
                      <div className="ac-actions ac-detail-section">
                        {confirmDelete ? (
                          <span className="ac-confirm">
                            <span className="ac-confirm-label">{t("adminCollections.confirmDelete")}</span>
                            <button className="ds-btn ds-btn--danger ds-btn--sm" disabled={busy} onClick={handleDelete}>
                              {t("adminCollections.delete")}
                            </button>
                            <button className="ds-btn ds-btn--secondary ds-btn--sm" onClick={() => setConfirmDelete(false)}>
                              {t("adminCollections.cancel")}
                            </button>
                          </span>
                        ) : (
                          <button type="button" className="ds-btn ds-btn--danger ds-btn--sm" onClick={() => setConfirmDelete(true)}>
                            {t("adminCollections.deleteCollection")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="ac-unassigned-view">
          <div className="ds-toolbar">
            <select
              className="ds-select"
              aria-label={t("adminCollections.moveToCollection")}
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              disabled={moving}
            >
              <option value="">{t("adminCollections.moveToCollection")}</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              type="button"
              className="ds-btn ds-btn--primary"
              disabled={moving || selected.size === 0 || !moveTo}
              onClick={moveSelected}
            >
              {moveLabel} ({selected.size})
            </button>
          </div>

          {unassigned.length === 0 && !loadingMore ? (
            <div className="ds-empty">{t("adminCollections.unassignedEmpty")}</div>
          ) : (
            <div className="ds-table-wrap">
              <table className="ds-table">
                <thead>
                  <tr>
                    <th className="ac-check-col" />
                    <th>{t("adminCollections.document")}</th>
                    <th>{t("adminCollections.edition")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unassigned.map((d) => (
                    <tr key={d.document_id} className={selected.has(d.document_id) ? "ac-row-selected" : ""}>
                      <td className="ac-check-col">
                        <input
                          type="checkbox"
                          aria-label={`${t("adminCollections.selectDocument")} ${d.filename}`}
                          checked={selected.has(d.document_id)}
                          onChange={() => toggleSelect(d.document_id)}
                        />
                      </td>
                      <td>{d.filename}</td>
                      <td>{d.edition || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {nextCursor && (
            <button type="button" className="ds-btn ds-btn--secondary ac-load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t("adminCollections.loadingMore") : t("adminCollections.loadMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
